import { useState } from 'react'

import { useFacilityFilterOptions } from '@/hooks/useFacilityFilterOptions'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import type { ColorScheme } from '@hivewatch/dark'
import { Flex } from '@hivewatch/dark'

import {
  DateRangeFilterField,
  MultiSelectFilterField,
} from '@/components/filter-drawer'
import { Role } from '@/features/managed-accounts'
import { useUsersQuery } from '@/graphql/generated/hooks'
import { FacilityType, GuardTourStatus } from '@/graphql/generated/schemas'
import {
  FilterOptionI,
  submittedAtIntervalOptions,
} from '@/utils/filterDrawers'
import { getUserOptions } from '@/utils/forms/optionBuilders'
import {
  MIN_CHAR_COUNT,
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import type { GuardToursFilterFormInputsI } from '../types/form'

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())

// Enum-derived, static (matches the legacy drawer's generated labels).
const statusOptions: FilterOptionI<string>[] = Object.values(
  GuardTourStatus
).map((value) => ({ value, label: toTitleCase(value) }))

interface DarkGuardToursFilterFormIProps {
  control: Control<GuardToursFilterFormInputsI>
  colorScheme: ColorScheme
}

export const DarkGuardToursFilterForm = ({
  control,
  colorScheme,
}: DarkGuardToursFilterFormIProps) => {
  const { t } = useTranslation()

  const {
    facilityOptions: facilityOptionsWithCue,
    isFacilitiesLoading,
    isFacilitiesError,
    facilitySearch,
    setFacilitySearch,
  } = useFacilityFilterOptions({ filter: { type: FacilityType.Normal } })

  const [guardSearch, setGuardSearch] = useState('')
  const [debouncedGuardSearch] = useDebounce(guardSearch, SEARCH_DEBOUNCE_TIME)

  const { data: guardsData, loading: isGuardsLoading } = useUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        roleName: Role.GUARD,
        ...(debouncedGuardSearch.length >= MIN_CHAR_COUNT
          ? {
              OR: [
                { firstNameLike: debouncedGuardSearch },
                { lastNameLike: debouncedGuardSearch },
                { usernameLike: debouncedGuardSearch },
                { fullNameLike: debouncedGuardSearch },
              ],
            }
          : {}),
      },
    },
  })

  const guardOptions = getUserOptions(guardsData)

  return (
    <Flex direction='column' gap='16px'>
      <MultiSelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='guardToursPage_filterDrawer_facility'
        isLoading={isFacilitiesLoading}
        label={t('guard_tours:filter_drawer.facility_name_label')}
        name='facilityIds'
        noOptionsMessage={({ inputValue }) =>
          getFacilityNoOptionsMessage(
            inputValue,
            isFacilitiesError,
            t('common:facility_search_error')
          )
        }
        onSearchChange={setFacilitySearch}
        options={facilityOptionsWithCue}
        placeholder={t('guard_tours:filter_drawer.facility_name_placeholder')}
        preserveOptionOrder
        searchValue={facilitySearch}
      />
      <MultiSelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='guardToursPage_filterDrawer_guard'
        isLoading={isGuardsLoading}
        label={t('guard_tours:filter_drawer.guard_name_label')}
        name='guardIds'
        onSearchChange={setGuardSearch}
        options={guardOptions}
        placeholder={t('guard_tours:filter_drawer.guard_name_placeholder')}
        searchValue={guardSearch}
      />
      <MultiSelectFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='guardToursPage_filterDrawer_status'
        label={t('guard_tours:filter_drawer.status_label')}
        name='status'
        options={statusOptions}
        placeholder={t('guard_tours:filter_drawer.status_placeholder')}
      />
      <DateRangeFilterField
        colorScheme={colorScheme}
        control={control}
        data-testid='guardToursPage_filterDrawer_submittedAt'
        label={t('guard_tours:filter_drawer.submitted_at_label')}
        name='submittedAt'
        options={submittedAtIntervalOptions}
        placeholder={t('guard_tours:filter_drawer.submitted_at_placeholder')}
      />
    </Flex>
  )
}
